import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ordersAPI } from '../services/api';
import Invoice from './Invoice';
import { Loader2, ArrowLeft, Printer } from 'lucide-react';

const InvoicePage = () => {
  const { id } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchOrder = async () => {
      try {
        setLoading(true);
        const response = await ordersAPI.getById(id);
        setOrder(response.data);
      } catch (err) {
        console.error('Error fetching order for invoice:', err);
        setError('Failed to load order details for the invoice.');
      } finally {
        setLoading(false);
      }
    };

    fetchOrder();
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-emerald-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600 font-medium">Preparing your invoice...</p>
        </div>
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center border border-gray-100">
          <div className="w-16 h-16 bg-red-50 text-red-600 rounded-full flex items-center justify-center mx-auto mb-6">
            <ArrowLeft className="w-8 h-8" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Invoice Error</h1>
          <p className="text-gray-600 mb-8">{error || 'Order not found.'}</p>
          <Link
            to="/orders"
            className="inline-flex items-center gap-2 px-6 py-3 bg-gray-900 text-white rounded-xl font-bold hover:bg-black transition-all"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Orders
          </Link>
        </div>
      </div>
    );
  }

  const isEligibleForInvoice = order.payment_status === 'success' || 
                               order.payment_status === 'paid' || 
                               ['confirmed', 'shipped', 'delivered'].includes(order.status);

  if (!isEligibleForInvoice) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center border border-gray-100">
          <div className="w-16 h-16 bg-yellow-50 text-yellow-600 rounded-full flex items-center justify-center mx-auto mb-6">
            <Printer className="w-8 h-8" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Invoice Not Available</h1>
          <p className="text-gray-600 mb-8">
            The invoice is currently unavailable. It will be generated once your payment is confirmed or your order is processed.
          </p>
          <Link
            to={`/orders/${id}`}
            className="inline-flex items-center gap-2 px-6 py-3 bg-emerald-600 text-white rounded-xl font-bold hover:bg-emerald-700 transition-all"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Order Tracking
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Top Bar for Navigation (Hidden when printing) */}
      <div className="bg-white border-b border-gray-200 py-4 px-6 print:hidden">
        <div className="max-w-4xl mx-auto flex justify-between items-center">
          <Link
            to={`/orders/${id}`}
            className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 font-medium transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Order Details
          </Link>
          <button
            onClick={() => window.print()}
            className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg font-bold hover:bg-emerald-700 transition-all shadow-sm"
          >
            <Printer className="w-4 h-4" />
            Print Now
          </button>
        </div>
      </div>

      {/* Invoice Content */}
      <div className="flex-1 overflow-auto">
        <Invoice order={order} />
      </div>
    </div>
  );
};

export default InvoicePage;
